import { z } from 'zod';
import {
  AckSchema,
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  MoneySchema,
  pageOf,
  withMeta,
} from '../common';
import { ExTradeRowSchema, PosRowSchema, SignalSchema } from '../dataset/core';
import { ProviderRowSchema, QuoteRowSchema, SignalRowSchema } from '../dataset/exec';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** execute.* — TradeHub, Execution Checklists, AI Signals, Trades, Copy Trading (SDD §7.4 Execute rows).
 *  Every order-shaped mutation passes the §13.4 admission funnel: session gate → lockout gate → intent claim (idempotencyKey)
 *  → broker dispatch under the reliability ladder → record. Same key while unconfirmed never re-dispatches. */

export const SideSchema = z.enum(['BUY', 'SELL']);
export const OrderTypeSchema = z.enum(['MARKET', 'LIMIT', 'STOP']);
export const SymbolSchema = z.string().min(3).max(12);
export const VolumeSchema = z.number().positive().max(100);
export const PriceSchema = z.number().positive();

export const PositionSchema = z
  .object({
    id: IdSchema,
    accountId: IdSchema,
    symbol: SymbolSchema,
    side: SideSchema,
    volume: z.number(),
    openPrice: z.number(),
    currentPrice: z.number(),
    sl: z.number().nullable(),
    tp: z.number().nullable(),
    pnl: MoneySchema,
    status: z.enum(['open', 'closed']),
    openedAt: IsoDateTimeSchema,
    closedAt: IsoDateTimeSchema.nullable(),
    closePrice: z.number().nullable(),
    strategyTag: z.string().nullable(),
    offPlan: z.boolean(),
  })
  .strict();
export type Position = z.infer<typeof PositionSchema>;
export const OrderSchema = z
  .object({
    id: IdSchema,
    intentId: IdSchema,
    accountId: IdSchema,
    symbol: SymbolSchema,
    side: SideSchema,
    type: OrderTypeSchema,
    volume: z.number(),
    price: z.number().nullable(),
    sl: z.number().nullable(),
    tp: z.number().nullable(),
    status: z.enum(['working', 'filled', 'cancelled', 'rejected']),
    placedAt: IsoDateTimeSchema,
    filledAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
export type Order = z.infer<typeof OrderSchema>;

/** Intent lifecycle (§13.4): claimed → done | failed | unconfirmed (reconciler owns the outcome). */
export const IntentStatusSchema = z.enum(['claimed', 'done', 'failed', 'unconfirmed']);
export const OrderIntentSchema = z
  .object({
    intentId: IdSchema,
    idempotencyKey: IdempotencyKeySchema,
    kind: z.enum(['market', 'pending', 'modify', 'close', 'partial', 'closeAll']),
    status: IntentStatusSchema,
    claimedAt: IsoDateTimeSchema,
    finalisedAt: IsoDateTimeSchema.nullable(),
    /** Refusal/failure reason when status = failed (user-safe). */
    reason: z.string().nullable(),
  })
  .strict();
export type OrderIntent = z.infer<typeof OrderIntentSchema>;

/** Result of an order-shaped mutation — the funnel outcome plus what was recorded. */
export const OrderResultSchema = withMeta({
  intent: OrderIntentSchema,
  order: OrderSchema.nullable(),
  positions: z.array(PositionSchema),
});
export type OrderResult = z.infer<typeof OrderResultSchema>;

const orderBase = {
  idempotencyKey: IdempotencyKeySchema,
  /** Optional: the armed session's own account when omitted.
   *
   *  The design's order desk has no account picker — a trader arms a session on an account and then trades it — so
   *  requiring the id on the wire made the client invent one. Omitting it is not ambiguity: the session determines the
   *  account, and there is at most one open session per account by construction (ADR-016). */
  accountId: IdSchema.nullable().optional(),
  symbol: SymbolSchema,
  side: SideSchema,
  volume: VolumeSchema,
  sl: PriceSchema.nullable().optional(),
  tp: PriceSchema.nullable().optional(),
  /** At-order strategy tag; `offPlan` marks a deliberate off-playbook entry (§7.4 TradeHub). */
  strategyTag: z.string().max(64).nullable().optional(),
  offPlan: z.boolean().optional(),
};
export const MarketOrderInputSchema = z.object(orderBase).strict();
export const PendingOrderInputSchema = z
  .object({
    ...orderBase,
    type: z.enum(['LIMIT', 'STOP']),
    price: PriceSchema,
    expiresAt: IsoDateTimeSchema.nullable().optional(),
  })
  .strict();
export const ModifyInputSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    positionId: IdSchema.nullable().optional(),
    orderId: IdSchema.nullable().optional(),
    sl: PriceSchema.nullable().optional(),
    tp: PriceSchema.nullable().optional(),
    price: PriceSchema.nullable().optional(),
    /** Break-even quick action: sets SL to the open price (§7.4 "BE quick action"). */
    breakEven: z.boolean().optional(),
  })
  .strict();
export const CloseInputSchema = z
  .object({ idempotencyKey: IdempotencyKeySchema, positionId: IdSchema })
  .strict();
export const PartialCloseInputSchema = z
  .object({ idempotencyKey: IdempotencyKeySchema, positionId: IdSchema, volume: VolumeSchema })
  .strict();
export const CloseAllInputSchema = z
  .object({ idempotencyKey: IdempotencyKeySchema, accountId: IdSchema.nullable().optional() })
  .strict();

export const PriceAlertSchema = z
  .object({
    id: IdSchema,
    symbol: SymbolSchema,
    price: z.number(),
    direction: z.enum(['above', 'below']),
    createdAt: IsoDateTimeSchema,
    triggeredAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
export const MonitorFlagSchema = z
  .object({ key: z.string(), severity: z.enum(['info', 'warning', 'danger']), text: z.string() })
  .strict();
export const LockoutSchema = z
  .object({ reason: z.string(), source: z.string(), unlocksAt: IsoDateTimeSchema.nullable() })
  .strict();

/** TradeHub snapshot: the order desk, positions, pending orders, favourites, alerts and the live gates. */
export const TradehubSnapshotSchema = withMeta({
  accountId: IdSchema,
  quotes: z.array(QuoteRowSchema),
  /** Reference display rows (pending / closed / orders tables). */
  rows: z
    .object({ pending: z.array(PosRowSchema), closed: z.array(PosRowSchema), orders: z.array(PosRowSchema) })
    .strict(),
  positions: z.array(PositionSchema),
  workingOrders: z.array(OrderSchema),
  favourites: z.array(SymbolSchema),
  alerts: z.array(PriceAlertSchema),
  session: z
    .object({
      state: z.string(),
      riskState: z.enum(['clear', 'watch', 'warning', 'locked']),
      tradesUsed: z.number().int(),
      maxTrades: z.number().int(),
    })
    .strict(),
  lockout: LockoutSchema.nullable(),
  monitorFlags: z.array(MonitorFlagSchema),
  engine: z.object({ status: z.enum(['running', 'degraded', 'stopped']) }).strict(),
});
export type TradehubSnapshot = z.infer<typeof TradehubSnapshotSchema>;

export const LiveCheckInputSchema = z
  .object({
    accountId: IdSchema,
    symbol: SymbolSchema,
    side: SideSchema,
    volume: VolumeSchema,
    price: PriceSchema.nullable().optional(),
    sl: PriceSchema.nullable().optional(),
    tp: PriceSchema.nullable().optional(),
  })
  .strict();
export const LiveCheckSchema = withMeta({
  allowed: z.boolean(),
  /** Named gate refusals (session / lockout / rule key) — error names the fix. */
  refusals: z.array(z.object({ gate: z.string(), message: z.string() }).strict()),
  risk: z
    .object({
      riskAmount: MoneySchema.nullable(),
      riskR: z.number().nullable(),
      rewardRisk: z.number().nullable(),
    })
    .strict(),
});

export const ChecklistPhaseSchema = z.enum(['pre', 'in', 'post']);
export const ExecChecklistSchema = z
  .object({
    id: IdSchema,
    phase: ChecklistPhaseSchema,
    items: z.array(z.object({ text: z.string(), done: z.boolean() }).strict()),
    positionId: IdSchema.nullable(),
    sessionId: IdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.nullable(),
    /** Speakable via the voice journal path (§20.3). */
    source: z.enum(['ui', 'voice', 'composer']),
  })
  .strict();

export const SignalOutcomeStatsSchema = withMeta({
  published: z.number().int(),
  accepted: z.number().int(),
  dismissed: z.number().int(),
  winRate: z.number(),
  avgR: z.number(),
});
export const SignalsSchema = withMeta({
  state: z.enum(['ready', 'scanning']),
  confidenceFloor: z.number(),
  cards: z.array(SignalSchema),
  rows: z.array(SignalRowSchema),
  dismissed: z.array(z.string()),
  /** Rule-suppressed state names the refusing rule (§7.4). */
  suppressedBy: z.string().nullable(),
});

export const TradeFiltersSchema = z
  .object({
    accountId: z.string().optional(),
    strategy: z.string().optional(),
    symbol: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
  })
  .strict();
export const TradesPageSchema = pageOf(ExTradeRowSchema).extend({
  strategies: z.array(z.string()),
  accounts: z.array(z.string()),
  stats: z
    .object({ total: z.number().int(), slTpTaggedRate: z.number(), winRate: z.number(), netPnl: MoneySchema })
    .strict(),
});
export const TradeDetailSchema = withMeta({
  row: ExTradeRowSchema,
  note: z.string().nullable(),
  strategy: z.string().nullable(),
  fills: z.array(z.object({ at: IsoDateTimeSchema, price: z.number(), volume: z.number() }).strict()),
});
export const ImportJobSchema = withMeta({
  jobId: IdSchema,
  status: z.enum(['queued', 'running', 'done', 'failed']),
  imported: z.number().int(),
});
export const ExportArtifactSchema = withMeta({
  artifactId: IdSchema,
  url: z.string(),
  format: z.enum(['csv', 'json']),
  expiresAt: IsoDateTimeSchema,
});

export const CopySettingsSchema = z
  .object({
    riskMultiplier: z.number().min(0.1).max(3),
    maxLots: z.number().positive().max(50),
    strategyFilters: z.array(z.string()),
    killSwitch: z.boolean(),
  })
  .strict();
export const CopyLinkSchema = withMeta({
  linkId: IdSchema.nullable(),
  state: z.enum(['disconnected', 'active', 'paused']),
  providerId: z.string().nullable(),
  accountId: IdSchema.nullable(),
  settings: CopySettingsSchema,
  /** Funded accounts cannot copy (§7.4 funded-account restriction). */
  restricted: z.boolean(),
  /** All-copies-rejected warning when filters exclude every provider strategy. */
  allCopiesRejected: z.boolean(),
});
export const CopyOrderSchema = z
  .object({
    id: IdSchema,
    at: IsoDateTimeSchema,
    symbol: SymbolSchema,
    side: SideSchema,
    volume: z.number(),
    status: z.enum(['executed', 'rejected']),
    reason: z.string().nullable(),
  })
  .strict();

export const executeProcedures = defineProcedures({
  'execute.tradehub.snapshot': query(
    'TradeHub snapshot: desk, positions, orders, alerts, gates',
    z.object({ accountId: IdSchema.optional() }).strict().optional(),
    TradehubSnapshotSchema,
  ),
  'execute.tradehub.liveCheck': query(
    'Pre-flight admission check + auto risk/R:R for the order desk',
    LiveCheckInputSchema,
    LiveCheckSchema,
  ),

  'execute.orders.market': mutation(
    'Market order through the §13.4 funnel',
    MarketOrderInputSchema,
    OrderResultSchema,
  ),
  'execute.orders.pending': mutation(
    'Pending (limit/stop) order through the funnel',
    PendingOrderInputSchema,
    OrderResultSchema,
  ),
  'execute.orders.modify': mutation('Modify SL/TP/price (funnel)', ModifyInputSchema, OrderResultSchema),
  'execute.orders.close': mutation('Close a position (funnel)', CloseInputSchema, OrderResultSchema),
  'execute.orders.partial': mutation(
    'Partially close a position (funnel)',
    PartialCloseInputSchema,
    OrderResultSchema,
  ),
  'execute.orders.closeAll': mutation('Close all positions (funnel)', CloseAllInputSchema, OrderResultSchema),
  'execute.orders.intent': query(
    'Confirm-by-read: the intent state for an idempotency key',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    withMeta({ intent: OrderIntentSchema.nullable(), positions: z.array(PositionSchema) }),
  ),

  'execute.favourites.list': query('Desk favourites', z.void(), withMeta({ symbols: z.array(SymbolSchema) })),
  'execute.favourites.add': mutation(
    'Add a favourite',
    z.object({ symbol: SymbolSchema }).strict(),
    withMeta({ symbols: z.array(SymbolSchema) }),
  ),
  'execute.favourites.remove': mutation(
    'Remove a favourite',
    z.object({ symbol: SymbolSchema }).strict(),
    withMeta({ symbols: z.array(SymbolSchema) }),
  ),
  'execute.alerts.list': query('Price alerts', z.void(), withMeta({ items: z.array(PriceAlertSchema) })),
  'execute.alerts.create': mutation(
    'Create a price alert',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        symbol: SymbolSchema,
        price: PriceSchema,
        direction: z.enum(['above', 'below']),
      })
      .strict(),
    PriceAlertSchema,
  ),
  'execute.alerts.delete': mutation('Delete a price alert', z.object({ id: IdSchema }).strict(), AckSchema),

  'execute.checklists.create': mutation(
    'Commit a pre/in/post-trade checklist (appends to session journal ≤ 3s)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        phase: ChecklistPhaseSchema,
        items: z
          .array(z.object({ text: z.string().min(1).max(160), done: z.boolean() }).strict())
          .min(1)
          .max(20),
        positionId: IdSchema.nullable().optional(),
        source: z.enum(['ui', 'voice', 'composer']).optional(),
      })
      .strict(),
    ExecChecklistSchema,
  ),
  'execute.checklists.active': query(
    'Checklists open in the current session',
    z.void(),
    withMeta({ items: z.array(ExecChecklistSchema) }),
  ),
  'execute.checklists.list': query(
    'Checklist history (cursor)',
    CursorInputSchema.optional(),
    pageOf(ExecChecklistSchema),
  ),
  'execute.checklists.stats': query(
    'Completion stats by phase',
    z.void(),
    withMeta({ completionRate: z.number(), byPhase: z.record(z.string(), z.number()) }),
  ),

  'execute.signals.list': query(
    'AI signals with confidence floor + dismissed state',
    z.void(),
    SignalsSchema,
  ),
  'execute.signals.dismiss': mutation(
    'Dismiss a signal',
    z.object({ symbol: SymbolSchema }).strict(),
    SignalsSchema,
  ),
  'execute.signals.restore': mutation(
    'Restore a dismissed signal',
    z.object({ symbol: SymbolSchema }).strict(),
    SignalsSchema,
  ),
  'execute.signals.outcomeStats': query('Signal outcome statistics', z.void(), SignalOutcomeStatsSchema),

  'execute.trades.list': query(
    'Trade ledger (cursor + filters) with filter vocabularies and stats bar',
    CursorInputSchema.extend({ filters: TradeFiltersSchema.optional() }).optional(),
    TradesPageSchema,
  ),
  'execute.trades.get': query(
    'One trade with note, strategy and fills',
    z.object({ id: z.number().int() }).strict(),
    TradeDetailSchema,
  ),
  'execute.trades.annotate': mutation(
    'Attach/replace a note on a trade',
    z
      .object({ idempotencyKey: IdempotencyKeySchema, id: z.number().int(), note: z.string().max(2000) })
      .strict(),
    TradeDetailSchema,
  ),
  'execute.trades.tagStrategy': mutation(
    'Bulk strategy tagging',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        ids: z.array(z.number().int()).min(1).max(200),
        strategy: z.string().nullable(),
      })
      .strict(),
    withMeta({ tagged: z.number().int() }),
  ),
  'execute.trades.importHistory': mutation(
    'Import broker history (job)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        accountId: IdSchema,
        source: z.enum(['broker', 'csv']),
      })
      .strict(),
    ImportJobSchema,
  ),
  'execute.trades.export': mutation(
    'Export trades as an artifact download',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        format: z.enum(['csv', 'json']),
        filters: TradeFiltersSchema.optional(),
      })
      .strict(),
    ExportArtifactSchema,
  ),

  'execute.copy.providers': query(
    'Copy-trading providers + strategy filter vocabulary',
    z.void(),
    withMeta({ items: z.array(ProviderRowSchema), strategies: z.array(z.string()) }),
  ),
  'execute.copy.link': mutation(
    'Connect to a provider (explicit, capped, revocable consent)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        providerId: z.string(),
        accountId: IdSchema,
        settings: CopySettingsSchema,
      })
      .strict(),
    CopyLinkSchema,
  ),
  'execute.copy.configure': mutation(
    'Change copy settings / filters / kill switch',
    z.object({ idempotencyKey: IdempotencyKeySchema, settings: CopySettingsSchema.partial() }).strict(),
    CopyLinkSchema,
  ),
  'execute.copy.pause': mutation(
    'Pause copying',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    CopyLinkSchema,
  ),
  'execute.copy.resume': mutation(
    'Resume copying',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    CopyLinkSchema,
  ),
  'execute.copy.unlink': mutation(
    'Disconnect from the provider',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    CopyLinkSchema,
  ),
  'execute.copy.activity': query(
    'Copy activity (executed / rejected with reason)',
    CursorInputSchema.optional(),
    pageOf(CopyOrderSchema),
  ),
});
export type ExecuteFamilyClient = RouterShape<typeof executeProcedures>;

export const executeFamily = defineFamily({
  name: 'execute',
  title: 'Execute',
  screens: ['TradeHub', 'Execution Checklists', 'AI Signals', 'Trades', 'Copy Trading'],
  procedures: executeProcedures,
  binding: 'simulated',
});
